import { first } from 'lodash'
import { useDeleteTableTabMutation } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { DetailedTableTab } from '~/components/tables/types'
import { Button } from '~/components/ui/atoms/button/Button'
import { Modal } from '~/components/ui/molecules/modal/Modal'
interface DeleteTableTabDialogProps {
  handleTabMenuClose: () => void
  setTabToDelete: (tab: DetailedTableTab | null) => void
  tabToDelete: DetailedTableTab
  tableName: string
  orderedTableTabs: DetailedTableTab[]
  handleTabChange: (tableTab: DetailedTableTab) => void
}

/**
 * Renders the dialog to delete a table tab
 */
export const DeleteTableTabDialog = ({
  handleTabMenuClose,
  setTabToDelete,
  tabToDelete,
  tableName,
  orderedTableTabs,
  handleTabChange,
}: DeleteTableTabDialogProps) => {
  const snackbar = useDossSnackbar()
  const { t } = useTypedTranslation()
  const [deleteTableTab] = useDeleteTableTabMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }
      // Set selected tab to one that has not just been deleted
      const filteredTabs = orderedTableTabs.filter((tab) => tab.id !== data.deleteTableTab.id)
      const firstTab = first(filteredTabs)
      if (firstTab) {
        handleTabChange(firstTab)
      }

      const normalizedId = cache.identify(data.deleteTableTab)
      cache.evict({ id: normalizedId })
      cache.gc()
    },
    onCompleted() {
      handleTabMenuClose()
    },
  })

  const handleDeleteTableTab = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    await deleteTableTab({ variables: { tableTabId: tabToDelete.id } })
      .then(() => {
        snackbar.showInfo(`${t('table.delete_table_tab_dialog.deleted_view')} ${tabToDelete.name}`)
        setTabToDelete(null)
      })
      .catch(() => {
        snackbar.showError(t('table.delete_table_tab_dialog.not_deleted'))
      })
  }

  const handleCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setTabToDelete(null)
    handleTabMenuClose()
  }

  return (
    <Modal
      actions={
        <Button onClick={handleDeleteTableTab} size="small" variant="danger">
          {t('common.actions.delete')}
        </Button>
      }
      onClose={handleCancelClick}
      open
      title={t('table.delete_table_tab_dialog.title')}
    >
      {t('table.delete_table_tab_dialog.delete_view', {
        viewName: tabToDelete.name,
        tableName,
      })}
    </Modal>
  )
}
